import {
	CarrierName,
	Dtos,
	FileExtension,
	FileType,
	IndemnityAttachmentTypeLabelMap,
	SuretyAccountUnderwritingStatus,
	SuretyType,
} from "@inrev/common";
import { useAdminGetCarrierPreviews } from "@portal/api/account/admin";
import {
	FormField,
	FormFieldGroup,
	FormItemLabel,
	FormRow,
	FormSection,
} from "@portal/components/form";
import {
	FormCheckboxOption,
	FormDropdown,
	FormMoney,
	FormPercent,
} from "@portal/components/form/fields";
import { Card } from "@portal/components/ui/Card";
import { LocalFileUpload } from "@portal/components/ui/LocalFileUpload";
import { Spinner } from "@portal/components/ui/Spinner";
import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { useEffect, useMemo, useState } from "react";
import { Path, useFormContext, useWatch } from "react-hook-form";
import type { LocallyUploadedFile } from "../../../../../../types";
import { carrierLogoIconMap } from "../../../../../../utils/carrier";
import { getSchemaDefaults } from "../../../../../../utils/form";
import { getMissingRequiredForApprovalLabels } from "../../../../../../utils/schema-fields";
import type { AdminCombinedFormData } from "../../schema";

const CreateAccount = Dtos.Admin.SuretyAccount.Create.Request;

type AccountTermsStepFormData = Pick<
	AdminCombinedFormData,
	"account" | "accountTerms" | "indemnityAgreement"
>;

const bondingLineDefaults = getSchemaDefaults(CreateAccount.bondingLineSchema);
const carrierBondingLineDefaults = getSchemaDefaults(CreateAccount.carrierBondingLineSchema);

const statusOptions = [
	{ label: "Approved", value: "approved" },
	{ label: "Declined", value: "declined" },
];

export const AccountTermsStep = () => {
	const { control, watch, setValue, getValues, formState } =
		useFormContext<AccountTermsStepFormData>();
	const termsStatus = useWatch({ control, name: "accountTerms.status" });
	const { allCarriers, allCarriersIsLoading } = useAdminGetCarrierPreviews();
	const schema = useFormSchema();
	const accountValues = watch("account");

	const approvalBlockerMessage = useMemo(() => {
		const missingLabels = schema
			? getMissingRequiredForApprovalLabels(schema, getValues(), "account")
			: [];

		if (!accountValues.modelSize) {
			missingLabels.push("Model Size");
		}
		if (!accountValues.details?.companies?.length) {
			missingLabels.push("Companies");
		}
		if (!accountValues.naicsCodes?.length) {
			missingLabels.push("Account Specialties");
		}

		if (missingLabels.length > 0) {
			return `Cannot approve with missing fields: ${missingLabels.join(", ")}`;
		}

		return null;
	}, [accountValues, schema]);

	const hasFormErrors = Object.keys(formState.errors).some(
		(key) => key !== "accountTerms" && key !== "indemnityAgreement",
	);
	const canApprove = approvalBlockerMessage === null;
	const statusDisabled = hasFormErrors;
	const disabledOptions = canApprove ? [] : ["approved"];

	useEffect(() => {
		if (allCarriersIsLoading || !allCarriers) return;
		const existingBondingLine = getValues("accountTerms.bondingLine");
		if (!existingBondingLine) {
			setValue("accountTerms.bondingLine", {
				...bondingLineDefaults,
				isPublished: false,
			});
		}
		const existingCarrierBondingLines = getValues("accountTerms.carrierBondingLines");
		if (!existingCarrierBondingLines || existingCarrierBondingLines.length === 0) {
			setValue(
				"accountTerms.carrierBondingLines",
				allCarriers.map((carrier) => ({
					...carrierBondingLineDefaults,
					suretyType: SuretyType.contract,
					carrierName: carrier.name,
					bondingLine: {
						...carrierBondingLineDefaults?.bondingLine,
					},
				})),
			);
		}
	}, [allCarriers, allCarriersIsLoading]);

	const carrierBondingLines = watch("accountTerms.carrierBondingLines") ?? [];

	const onAccountBondingLineMatchClick = () => {
		const bondingLine = getValues("accountTerms.bondingLine");
		setValue(
			"accountTerms.carrierBondingLines",
			carrierBondingLines.map((cbl) => ({
				...cbl,
				bondingLine: {
					...cbl.bondingLine,
					rate: bondingLine?.rate,
					singleLimit: bondingLine?.singleLimit,
					aggregateLimit: bondingLine?.aggregateLimit,
				},
			})),
			{ shouldValidate: true },
		);
	};

	const indemnityAgreementFile = watch("indemnityAgreement");
	const [indemnityAgreementFileUpload, setIndemnityAgreementFileUpload] = useState<
		LocallyUploadedFile<typeof FileType.indemnity_agreement>[]
	>(
		indemnityAgreementFile
			? [
					{
						id: indemnityAgreementFile.fileId,
						name: indemnityAgreementFile.name,
						extension: FileExtension.pdf,
						types: [FileType.indemnity_agreement],
					},
				]
			: [],
	);

	useEffect(() => {
		if (indemnityAgreementFileUpload.length > 0) {
			setValue("indemnityAgreement", {
				fileId: indemnityAgreementFileUpload[0].id,
				name: indemnityAgreementFileUpload[0].name,
			});
		} else {
			setValue("indemnityAgreement", null);
		}
	}, [indemnityAgreementFileUpload.length]);

	return (
		<div className="w-full flex flex-col space-y-[48px] pt-[18px] pb-[125px]">
			<div className="flex flex-col w-full space-y-[36px]">
				<FormSection topPadding header="Account Terms">
					<FormFieldGroup condensed className="space-y-[15px] flex-[2]">
						<FormRow>
							<FormField condensed>
								<FormItemLabel condensed>Status</FormItemLabel>
								{statusDisabled && (
									<div className="text-yellow-700 text-[12px]">
										Please resolve all form errors to select a status
									</div>
								)}
								{!statusDisabled && approvalBlockerMessage && (
									<div className="text-yellow-700 text-[12px]">{approvalBlockerMessage}</div>
								)}
								<FormDropdown
									name={"accountTerms.status"}
									control={control}
									options={statusOptions}
									disabled={statusDisabled}
									disabledOptions={disabledOptions}
									className="flex-grow"
									buttonClassName="bg-surface-base"
									compact
									placeholder="Select a status"
									deselectable
								/>
							</FormField>
						</FormRow>
						{termsStatus === SuretyAccountUnderwritingStatus.approved &&
							(allCarriersIsLoading ? (
								<Spinner />
							) : (
								<>
									<BondingLineCard
										carrierName={"inrev" as CarrierName}
										paths={{
											rate: "accountTerms.bondingLine.rate",
											singleLimit: "accountTerms.bondingLine.singleLimit",
											aggregateLimit: "accountTerms.bondingLine.aggregateLimit",
											isPublished: "accountTerms.bondingLine.isPublished",
										}}
										onMatchClick={onAccountBondingLineMatchClick}
									/>
									{allCarriers?.map((carrier, index) => (
										<BondingLineCard
											key={carrier.name}
											carrierName={carrier.name}
											paths={{
												rate: `accountTerms.carrierBondingLines.${index}.bondingLine.rate`,
												singleLimit: `accountTerms.carrierBondingLines.${index}.bondingLine.singleLimit`,
												aggregateLimit: `accountTerms.carrierBondingLines.${index}.bondingLine.aggregateLimit`,
											}}
										/>
									))}
								</>
							))}
					</FormFieldGroup>
				</FormSection>
				<FormSection topPadding header="Indemnity">
					<FormField>
						<FormItemLabel>
							Upload an indemnity agreement to activate the account upon submission.
						</FormItemLabel>
						<LocalFileUpload
							value={indemnityAgreementFileUpload}
							allowedTypesAndLabels={{
								[FileType.indemnity_agreement]:
									IndemnityAttachmentTypeLabelMap[FileType.indemnity_agreement],
							}}
							maxFiles={1}
							allowDelete={true}
							allowedExtensions={[FileExtension.pdf]}
							onChange={setIndemnityAgreementFileUpload}
							analyticsContext="submission-upload"
						/>
					</FormField>
				</FormSection>
			</div>
		</div>
	);
};

const BondingLineCard = ({
	carrierName,
	paths,
	onMatchClick,
}: {
	carrierName: CarrierName;
	paths: {
		rate?: Path<AccountTermsStepFormData>;
		singleLimit?: Path<AccountTermsStepFormData>;
		aggregateLimit?: Path<AccountTermsStepFormData>;
		isPublished?: Path<AccountTermsStepFormData>;
	};
	onMatchClick?: () => void;
}) => {
	const { control } = useFormContext<AccountTermsStepFormData>();
	return (
		<Card className="flex flex-col space-y-[12px] p-[30px] pt-[25px]">
			<FormFieldGroup condensed>
				<div className="flex items-center space-x-[15px]">
					{carrierLogoIconMap[carrierName]}
					{!!paths.isPublished && (
						<FormRow className="justify-end items-center">
							<FormCheckboxOption
								name={paths.isPublished}
								control={control}
								label="Publish"
								compact
							/>
						</FormRow>
					)}
				</div>
				{!!paths.rate && (
					<FormField condensed>
						<FormItemLabel condensed required>
							Rate
						</FormItemLabel>
						<FormPercent
							name={paths.rate}
							control={control}
							allowDecimals
							decimalPlaces={3}
							compact
							errorMessage="Required"
						/>
					</FormField>
				)}
				<FormRow>
					{!!paths.singleLimit && (
						<FormField condensed>
							<FormItemLabel condensed required>
								Single Limit
							</FormItemLabel>
							<FormMoney
								name={paths.singleLimit}
								control={control}
								compact
								errorMessage="Required"
							/>
						</FormField>
					)}
					{!!paths.aggregateLimit && (
						<FormField condensed>
							<FormItemLabel condensed required>
								Aggregate Limit
							</FormItemLabel>
							<FormMoney
								name={paths.aggregateLimit}
								control={control}
								compact
								errorMessage="Required"
							/>
						</FormField>
					)}
				</FormRow>
			</FormFieldGroup>
			{!!onMatchClick && (
				<div className="flex divide-x divide-x-gray-200 pt-[7px]">
					<span
						className="text-[12px] text-tertiary font-normal hover:underline cursor-pointer"
						onClick={onMatchClick}
					>
						Match
					</span>
				</div>
			)}
		</Card>
	);
};
